import React, {
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

import {
  Link,
  useParams,
} from 'react-router-dom';

import './ResultFrameworkEditorPage.css';

/* global process */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;

const ADMIN_BASE =
  `${API_BASE_URL}/api/v1/admin`;

const EMPTY_RESULT = {
  parent_id: null,
  code: '',
  level: '',
  title_fr: '',
  title_en: '',
  description_fr: '',
  description_en: '',
  sort_order: 1,
  active: true,
};

const EMPTY_INDICATOR = {
  code: '',
  description_fr: '',
  description_en: '',
  baseline_year: '',
  baseline_value: '',
  target_value: '',
  target_year: '',
  unit: '',
  notes: '',
  sort_order: 1,
  active: true,
};

const ResultFrameworkEditorPage = () => {
  const { id } = useParams();

  const [framework, setFramework] =
    useState(null);

  const [status, setStatus] =
    useState('loading');

  const [message, setMessage] =
    useState('');

  const [error, setError] =
    useState('');

  const [editingResultId, setEditingResultId] =
    useState(null);

  const [resultForm, setResultForm] =
    useState(EMPTY_RESULT);

  const [
    editingIndicatorId,
    setEditingIndicatorId,
  ] = useState(null);

  const [indicatorResultId, setIndicatorResultId] =
    useState(null);

  const [indicatorForm, setIndicatorForm] =
    useState(EMPTY_INDICATOR);

  const loadFramework = async () => {
    try {
      const response =
        await axios.get(
          `${ADMIN_BASE}/result_frameworks/${id}`
        );

      setFramework(response.data);
      setStatus('ready');
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to load framework.'
      );

      setStatus('failed');
    }
  };

  useEffect(() => {
    loadFramework();
  }, [id]);

  const updateFrameworkField = (event) => {
    const { name, value } = event.target;

    setFramework((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const saveFramework = async () => {
    try {
      setMessage('');
      setError('');

      const response =
        await axios.patch(
          `${ADMIN_BASE}/result_frameworks/${id}`,
          {
            result_framework: {
              version: framework.version,
              start_year:
                framework.start_year,
              end_year:
                framework.end_year,
              status: framework.status,
              title_fr:
                framework.title_fr,
              title_en:
                framework.title_en,
              description_fr:
                framework.description_fr,
              description_en:
                framework.description_en,
            },
          }
        );

      setFramework(response.data);
      setMessage(
        'Framework information saved.'
      );
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to save framework.'
      );
    }
  };

  const startNewResult = (
    parentId = null
  ) => {
    setEditingResultId('new');

    setResultForm({
      ...EMPTY_RESULT,
      parent_id: parentId,
      sort_order:
        framework.results.length + 1,
    });
  };

  const startEditResult = (result) => {
    setEditingResultId(result.id);

    setResultForm({
      parent_id: result.parent_id,
      code: result.code || '',
      level: result.level || '',
      title_fr: result.title_fr || '',
      title_en: result.title_en || '',
      description_fr:
        result.description_fr || '',
      description_en:
        result.description_en || '',
      sort_order:
        result.sort_order || 1,
      active:
        Boolean(result.active),
    });
  };

  const handleResultChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setResultForm((current) => ({
      ...current,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));
  };

  const saveResult = async () => {
    try {
      setError('');
      setMessage('');

      if (editingResultId === 'new') {
        await axios.post(
          `${ADMIN_BASE}/result_frameworks/${id}/results`,
          {
            result: resultForm,
          }
        );
      } else {
        await axios.patch(
          `${ADMIN_BASE}/results/${editingResultId}`,
          {
            result: resultForm,
          }
        );
      }

      setEditingResultId(null);
      setResultForm(EMPTY_RESULT);

      await loadFramework();

      setMessage('Result saved.');
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to save result.'
      );
    }
  };

  const deleteResult = async (
    result
  ) => {
    const confirmed = window.confirm(
      `Delete result ${result.code || result.id}?`
    );

    if (!confirmed) return;

    try {
      await axios.delete(
        `${ADMIN_BASE}/results/${result.id}`
      );

      await loadFramework();

      setMessage('Result deleted.');
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to delete result.'
      );
    }
  };

  const startNewIndicator = (
    resultId
  ) => {
    setEditingIndicatorId('new');
    setIndicatorResultId(resultId);

    setIndicatorForm({
      ...EMPTY_INDICATOR,
    });
  };

  const startEditIndicator = (
    resultId,
    indicator
  ) => {
    setEditingIndicatorId(
      indicator.id
    );

    setIndicatorResultId(resultId);

    setIndicatorForm({
      code: indicator.code || '',
      description_fr:
        indicator.description_fr || '',
      description_en:
        indicator.description_en || '',
      baseline_year:
        indicator.baseline_year || '',
      baseline_value:
        indicator.baseline_value || '',
      target_value:
        indicator.target_value || '',
      target_year:
        indicator.target_year || '',
      unit: indicator.unit || '',
      notes: indicator.notes || '',
      sort_order:
        indicator.sort_order || 1,
      active:
        Boolean(indicator.active),
    });
  };

  const handleIndicatorChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setIndicatorForm((current) => ({
      ...current,
      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));
  };

  const saveIndicator = async () => {
    try {
      setError('');
      setMessage('');

      if (
        editingIndicatorId === 'new'
      ) {
        await axios.post(
          `${ADMIN_BASE}/results/${indicatorResultId}/indicators`,
          {
            indicator: indicatorForm,
          }
        );
      } else {
        await axios.patch(
          `${ADMIN_BASE}/indicators/${editingIndicatorId}`,
          {
            indicator: indicatorForm,
          }
        );
      }

      setEditingIndicatorId(null);
      setIndicatorResultId(null);
      setIndicatorForm(
        EMPTY_INDICATOR
      );

      await loadFramework();

      setMessage('Indicator saved.');
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to save indicator.'
      );
    }
  };

  const deleteIndicator = async (
    indicator
  ) => {
    const confirmed = window.confirm(
      `Delete indicator ` +
      `${indicator.code || indicator.id}?`
    );

    if (!confirmed) return;

    try {
      await axios.delete(
        `${ADMIN_BASE}/indicators/${indicator.id}`
      );

      await loadFramework();

      setMessage('Indicator deleted.');
    } catch (requestError) {
      setError(
        requestError.message ||
        'Unable to delete indicator.'
      );
    }
  };

  const roots =
    framework?.results.filter(
      (result) => !result.parent_id
    ) || [];

  const childrenOf = (parentId) =>
    framework.results.filter(
      (result) =>
        result.parent_id === parentId
    );

  const renderIndicators = (
    result
  ) => (
    <div className="rfe-indicators">
      <div className="rfe-subheading">
        <strong>Indicators</strong>

        <button
          type="button"
          onClick={() =>
            startNewIndicator(
              result.id
            )
          }
        >
          + Indicator
        </button>
      </div>

      {result.indicators.map(
        (indicator) => (
          <article
            key={indicator.id}
            className="rfe-indicator"
          >
            <div>
              <strong>
                {indicator.code ||
                  `Indicator ${indicator.id}`}
              </strong>

              <p>
                {
                  indicator.description_en
                }
              </p>

              <small>
                Target:{' '}
                {
                  indicator.target_value ||
                  '—'
                }
                {' '}
                {
                  indicator.unit || ''
                }
                {
                  indicator.target_year
                    ? ` by ${indicator.target_year}`
                    : ''
                }
              </small>
            </div>

            <div className="rfe-mini-actions">
              <button
                type="button"
                onClick={() =>
                  startEditIndicator(
                    result.id,
                    indicator
                  )
                }
              >
                Edit
              </button>

              <button
                type="button"
                onClick={() =>
                  deleteIndicator(
                    indicator
                  )
                }
              >
                Delete
              </button>
            </div>
          </article>
        )
      )}

      {editingIndicatorId &&
       indicatorResultId ===
         result.id && (
        <div className="rfe-form-box">
          <h4>
            {editingIndicatorId ===
            'new'
              ? 'Add indicator'
              : 'Edit indicator'}
          </h4>

          <div className="rfe-form-grid">
            <label>
              <span>Code</span>
              <input
                name="code"
                value={
                  indicatorForm.code
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label>
              <span>Unit</span>
              <input
                name="unit"
                value={
                  indicatorForm.unit
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label className="rfe-full">
              <span>
                Description EN
              </span>
              <textarea
                rows="3"
                name="description_en"
                value={
                  indicatorForm
                    .description_en
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label className="rfe-full">
              <span>
                Description FR
              </span>
              <textarea
                rows="3"
                name="description_fr"
                value={
                  indicatorForm
                    .description_fr
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label>
              <span>Baseline year</span>
              <input
                type="number"
                name="baseline_year"
                value={
                  indicatorForm
                    .baseline_year
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label>
              <span>Baseline value</span>
              <input
                name="baseline_value"
                value={
                  indicatorForm
                    .baseline_value
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label>
              <span>Target value</span>
              <input
                name="target_value"
                value={
                  indicatorForm
                    .target_value
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label>
              <span>Target year</span>
              <input
                type="number"
                name="target_year"
                value={
                  indicatorForm
                    .target_year
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label>
              <span>Sort order</span>
              <input
                type="number"
                name="sort_order"
                value={
                  indicatorForm
                    .sort_order
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>

            <label className="rfe-checkbox">
              <input
                type="checkbox"
                name="active"
                checked={
                  indicatorForm.active
                }
                onChange={
                  handleIndicatorChange
                }
              />
              <span>Active</span>
            </label>

            <label className="rfe-full">
              <span>Notes</span>
              <textarea
                rows="3"
                name="notes"
                value={
                  indicatorForm.notes
                }
                onChange={
                  handleIndicatorChange
                }
              />
            </label>
          </div>

          <div className="rfe-form-actions">
            <button
              type="button"
              onClick={saveIndicator}
            >
              Save indicator
            </button>

            <button
              type="button"
              className="secondary"
              onClick={() => {
                setEditingIndicatorId(
                  null
                );
                setIndicatorResultId(
                  null
                );
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderResult = (
    result,
    depth = 0
  ) => (
    <article
      key={result.id}
      className="rfe-result-card"
      style={{
        marginLeft:
          `${depth * 24}px`,
      }}
    >
      <div className="rfe-result-header">
        <div>
          <div className="rfe-result-badges">
            <span>
              {result.code ||
                `Result ${result.id}`}
            </span>

            <span>
              {result.level}
            </span>

            {!result.active && (
              <span className="inactive">
                Inactive
              </span>
            )}
          </div>

          <h3>
            {result.title_en}
          </h3>

          <p>
            {result.description_en}
          </p>
        </div>

        <div className="rfe-result-actions">
          <button
            type="button"
            onClick={() =>
              startEditResult(
                result
              )
            }
          >
            Edit
          </button>

          <button
            type="button"
            onClick={() =>
              startNewResult(
                result.id
              )
            }
          >
            + Child
          </button>

          <button
            type="button"
            onClick={() =>
              deleteResult(
                result
              )
            }
          >
            Delete
          </button>
        </div>
      </div>

      {editingResultId ===
        result.id && (
        renderResultForm()
      )}

      {renderIndicators(result)}

      {childrenOf(result.id).map(
        (child) =>
          renderResult(
            child,
            depth + 1
          )
      )}
    </article>
  );

  const renderResultForm = () => (
    <div className="rfe-form-box">
      <h4>
        {editingResultId === 'new'
          ? 'Add result'
          : 'Edit result'}
      </h4>

      <div className="rfe-form-grid">
        <label>
          <span>Code</span>
          <input
            name="code"
            value={resultForm.code}
            onChange={
              handleResultChange
            }
          />
        </label>

        <label>
          <span>Level</span>
          <input
            name="level"
            value={resultForm.level}
            onChange={
              handleResultChange
            }
            placeholder="Outcome, Output..."
          />
        </label>

        <label className="rfe-full">
          <span>Title EN</span>
          <input
            name="title_en"
            value={resultForm.title_en}
            onChange={
              handleResultChange
            }
          />
        </label>

        <label className="rfe-full">
          <span>Title FR</span>
          <input
            name="title_fr"
            value={resultForm.title_fr}
            onChange={
              handleResultChange
            }
          />
        </label>

        <label className="rfe-full">
          <span>Description EN</span>
          <textarea
            rows="4"
            name="description_en"
            value={
              resultForm.description_en
            }
            onChange={
              handleResultChange
            }
          />
        </label>

        <label className="rfe-full">
          <span>Description FR</span>
          <textarea
            rows="4"
            name="description_fr"
            value={
              resultForm.description_fr
            }
            onChange={
              handleResultChange
            }
          />
        </label>

        <label>
          <span>Sort order</span>
          <input
            type="number"
            name="sort_order"
            value={
              resultForm.sort_order
            }
            onChange={
              handleResultChange
            }
          />
        </label>

        <label className="rfe-checkbox">
          <input
            type="checkbox"
            name="active"
            checked={
              resultForm.active
            }
            onChange={
              handleResultChange
            }
          />
          <span>Active</span>
        </label>
      </div>

      <div className="rfe-form-actions">
        <button
          type="button"
          onClick={saveResult}
        >
          Save result
        </button>

        <button
          type="button"
          className="secondary"
          onClick={() => {
            setEditingResultId(null);
            setResultForm(
              EMPTY_RESULT
            );
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  if (
    status === 'loading' ||
    !framework
  ) {
    return (
      <main className="admin-page">
        <p>
          Loading results framework...
        </p>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">
            Results Framework
          </p>

          <h1>
            Pillar {framework.pilar_id}
          </h1>

          <p>
            Edit the strategic framework,
            results hierarchy and indicators.
          </p>
        </div>

        <Link
          to="/admin/results-framework"
          className="admin-secondary-link"
        >
          ← Frameworks
        </Link>
      </header>

      {message && (
        <div className="rfe-message success">
          {message}
        </div>
      )}

      {error && (
        <div className="rfe-message error">
          {error}
        </div>
      )}

      <section className="rfe-framework-editor">
        <div className="rfe-section-title">
          <span>Framework information</span>

          <button
            type="button"
            onClick={saveFramework}
          >
            Save framework
          </button>
        </div>

        <div className="rfe-form-grid">
          <label>
            <span>Version</span>
            <input
              name="version"
              value={
                framework.version || ''
              }
              onChange={
                updateFrameworkField
              }
            />
          </label>

          <label>
            <span>Status</span>
            <select
              name="status"
              value={
                framework.status
              }
              onChange={
                updateFrameworkField
              }
            >
              <option value="active">
                Active
              </option>

              <option value="draft">
                Draft
              </option>

              <option value="archived">
                Archived
              </option>
            </select>
          </label>

          <label>
            <span>Start year</span>
            <input
              type="number"
              name="start_year"
              value={
                framework.start_year ||
                ''
              }
              onChange={
                updateFrameworkField
              }
            />
          </label>

          <label>
            <span>End year</span>
            <input
              type="number"
              name="end_year"
              value={
                framework.end_year ||
                ''
              }
              onChange={
                updateFrameworkField
              }
            />
          </label>

          <label className="rfe-full">
            <span>Title EN</span>
            <input
              name="title_en"
              value={
                framework.title_en ||
                ''
              }
              onChange={
                updateFrameworkField
              }
            />
          </label>

          <label className="rfe-full">
            <span>Title FR</span>
            <input
              name="title_fr"
              value={
                framework.title_fr ||
                ''
              }
              onChange={
                updateFrameworkField
              }
            />
          </label>

          <label className="rfe-full">
            <span>Description EN</span>
            <textarea
              rows="5"
              name="description_en"
              value={
                framework
                  .description_en ||
                ''
              }
              onChange={
                updateFrameworkField
              }
            />
          </label>

          <label className="rfe-full">
            <span>Description FR</span>
            <textarea
              rows="5"
              name="description_fr"
              value={
                framework
                  .description_fr ||
                ''
              }
              onChange={
                updateFrameworkField
              }
            />
          </label>
        </div>
      </section>

      <section className="rfe-results-section">
        <div className="rfe-section-title">
          <div>
            <span>
              Results hierarchy
            </span>

            <small>
              {framework.results.length}
              {' results'}
            </small>
          </div>

          <button
            type="button"
            onClick={() =>
              startNewResult(null)
            }
          >
            + Add top-level result
          </button>
        </div>

        {editingResultId ===
          'new' &&
          resultForm.parent_id ===
            null &&
          renderResultForm()}

        {roots.map(
          (result) =>
            renderResult(result)
        )}
      </section>
    </main>
  );
};

export default ResultFrameworkEditorPage;
